import { CenteredLoader } from '@/components'
import { cn } from '@/lib/utils'
import { LazyRouteMap } from '@/modules/salary/work-period-map/lazy-route-map'
import type { VehicleRoute } from '@/modules/salary/work-period-map/types'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, parseISO } from 'date-fns'
import { useMemo, useState } from 'react'
import { GradePill, InfoLabel, PerfectScoreCard, ScoreBar } from './grade-parts'
import type { HighlightSpan } from './metrics'
import { eventHighlights, formatNumber, metricHighlights } from './metrics'
import { DRIVER_GRADE_DAY_DETAIL_QUERY } from './queries'
import { StretchStrip } from './stretch-strip'
import { OVERSPEED_KEY, TrialMeasures } from './trial-measures'
import type { GradeMetric } from './use-metric-texts'
import { useMetricTexts } from './use-metric-texts'

const ROUTE_COLOR = '#78716c'
// A selected overspeed event is shown with the road either side of it.
const EVENT_FOCUS_PADDING_MS = 10 * 60_000

const MetricRow: React.FC<{
  metric: GradeMetric
  metrics: GradeMetric[]
  active: boolean
  pinned: boolean
  onHover(key: string | null): void
  onPin(key: string): void
}> = ({ metric, metrics, active, pinned, onHover, onPin }) => {
  const translations = useTranslate({
    points: ['driver-grade.points-gain', { points: '' }],
  })
  const { label, info } = useMetricTexts()

  return (
    <div
      role="button"
      tabIndex={0}
      onMouseEnter={() => onHover(metric.key)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onPin(metric.key)}
      onKeyDown={event => event.key === 'Enter' && onPin(metric.key)}
      className={cn(
        'cursor-pointer space-y-2 rounded-xl border px-4 py-3 transition-colors',
        active
          ? 'border-stone-400 bg-stone-50'
          : 'border-transparent hover:bg-stone-50',
        pinned && 'border-stone-900'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <InfoLabel label={label(metric.key)} info={info(metric, metrics)} />
        <span className="text-sm font-semibold tabular-nums text-stone-900">
          {formatNumber(metric.value)} %
        </span>
      </div>
      <ScoreBar score={metric.score} />
      <div className="flex justify-between text-xs tabular-nums text-stone-500">
        <span>{formatNumber(metric.score)} / 100</span>
        {metric.pointsToGain > 0 && (
          <span className="text-emerald-700">
            {translations.points({ points: formatNumber(metric.pointsToGain) })}
          </span>
        )}
      </div>
    </div>
  )
}

export const DriverGradeDayInspector: React.FC<{
  user: string
  date: string
}> = ({ user, date }) => {
  const translations = useTranslate({
    grade: 'driver-grade.grade',
    gradeInfo: 'driver-grade.info-grade',
    hoverHint: 'driver-grade.hover-hint',
    noRoute: 'driver-grade.no-route',
    skipped: {
      below_minimum_distance: 'driver-grade.skipped-below_minimum_distance',
      insufficient_metrics: 'driver-grade.skipped-insufficient_metrics',
      sparse_accumulators: 'driver-grade.skipped-sparse_accumulators',
    },
  })

  const { data, loading } = useQuery(DRIVER_GRADE_DAY_DETAIL_QUERY, {
    variables: { user, date },
  })
  const detail = data?.driverGradeDayDetail

  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null)
  const [pinnedMetric, setPinnedMetric] = useState<string | null>(null)
  const [chosenGroup, setChosenGroup] = useState<string | null>(null)
  const [hoveredStretch, setHoveredStretch] = useState<number | null>(null)
  const [pinnedStretch, setPinnedStretch] = useState<number | null>(null)
  const [pinnedEvent, setPinnedEvent] = useState<number | null>(null)
  const activeMetric = hoveredMetric ?? pinnedMetric
  const activeStretch = hoveredStretch ?? pinnedStretch
  const overspeedEvents = useMemo(
    () => detail?.overspeedEvents ?? [],
    [detail?.overspeedEvents]
  )

  const pinMetric = (key: string) => {
    setPinnedMetric(current => (current === key ? null : key))
    setChosenGroup(null)
    setPinnedStretch(null)
    setPinnedEvent(null)
  }

  const selectEvent = (index: number | null) => {
    setPinnedEvent(index)
    setPinnedMetric(OVERSPEED_KEY)
    setPinnedStretch(null)
  }

  // Grouped by the part that costs the most points until one is picked.
  const scoredPin = detail?.metrics.some(metric => metric.key === pinnedMetric)
    ? pinnedMetric
    : null
  const groupMetric =
    chosenGroup ??
    scoredPin ??
    [...(detail?.metrics ?? [])].sort(
      (a, b) => b.pointsToGain - a.pointsToGain
    )[0]?.key ??
    'idle_share'
  const stretches = useMemo(
    () =>
      (detail?.stretches ?? []).filter(
        stretch => stretch.metricKey === groupMetric
      ),
    [detail?.stretches, groupMetric]
  )

  const chooseGroup = (key: string) => {
    setChosenGroup(key)
    setHoveredStretch(null)
    setPinnedStretch(null)
  }

  const routes = useMemo<VehicleRoute[]>(
    () =>
      (detail?.routes ?? []).map(route => ({
        vehicleVin: route.vehicleVin ?? null,
        label: route.vehicleRegistrationNumber || route.vehicleVin || '',
        color: ROUTE_COLOR,
        samples: route.coordinates,
      })),
    [detail?.routes]
  )

  // The map shows every segment in its own colour, also inside a stretch the
  // strip has grouped under another tone: a good spell swallowed by a poor
  // stretch is still good where it was driven.
  const highlights = useMemo<HighlightSpan[] | null>(() => {
    const segments = detail?.segments ?? []
    if (activeMetric === OVERSPEED_KEY) {
      const event =
        hoveredMetric === null && pinnedEvent !== null
          ? overspeedEvents[pinnedEvent]
          : null
      return eventHighlights(event ? [event] : overspeedEvents)
    }
    const stretch = activeStretch !== null ? stretches[activeStretch] : null
    if (stretch)
      return metricHighlights(
        segments.slice(stretch.firstSegment, stretch.lastSegment + 1),
        stretch.metricKey
      )
    return activeMetric ? metricHighlights(segments, activeMetric) : null
  }, [
    activeMetric,
    activeStretch,
    detail?.segments,
    hoveredMetric,
    overspeedEvents,
    pinnedEvent,
    stretches,
  ])

  const focus = useMemo(() => {
    const event = pinnedEvent !== null ? overspeedEvents[pinnedEvent] : null
    if (event) {
      const [span] = eventHighlights([event], EVENT_FOCUS_PADDING_MS)
      return span ? { start: span.start, end: span.end } : null
    }
    const stretch = pinnedStretch !== null ? stretches[pinnedStretch] : null
    return stretch
      ? {
          start: new Date(stretch.datetimeStart),
          end: new Date(stretch.datetimeEnd),
        }
      : null
  }, [overspeedEvents, pinnedEvent, pinnedStretch, stretches])

  if (loading && !detail)
    return (
      <div className="flex h-64 items-center justify-center rounded-2xl bg-white shadow-card">
        <CenteredLoader />
      </div>
    )
  if (!detail) return null

  const metrics = detail.metrics
  const skippedText =
    detail.skippedReason &&
    translations.skipped[
      detail.skippedReason as keyof typeof translations.skipped
    ]

  const trialMeasures = (
    <TrialMeasures
      observations={detail.observations}
      overspeedEvents={overspeedEvents}
      active={activeMetric === OVERSPEED_KEY}
      pinned={pinnedMetric === OVERSPEED_KEY}
      pinnedEvent={pinnedEvent}
      onHover={setHoveredMetric}
      onPin={pinMetric}
      onSelectEvent={selectEvent}
    />
  )

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      <div className="overflow-hidden rounded-2xl bg-white shadow-card">
        <div className="flex items-start justify-between gap-3 px-5 py-4">
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
              {format(parseISO(detail.date), 'dd.MM.yyyy')}
            </div>
            <div className="text-sm text-stone-600">
              {formatNumber(detail.distanceKm, 0)} km
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <InfoLabel
              label={translations.grade}
              info={translations.gradeInfo}
            />
            <GradePill grade={detail.grade} />
          </div>
        </div>
        {skippedText && (
          <div className="border-t border-stone-200 bg-amber-50 px-5 py-3 text-sm text-amber-900">
            {skippedText}
          </div>
        )}
        <div className="space-y-2 border-t border-stone-200 px-3 py-3">
          <p className="px-2 text-xs text-stone-500">
            {translations.hoverHint}
          </p>
          {metrics.map(metric => (
            <MetricRow
              key={metric.key}
              metric={metric}
              metrics={metrics}
              active={activeMetric === metric.key}
              pinned={pinnedMetric === metric.key}
              onHover={setHoveredMetric}
              onPin={pinMetric}
            />
          ))}
        </div>
        {stretches.length > 0 && (
          <div className="border-t border-stone-200 px-5 py-4">
            <StretchStrip
              date={detail.date}
              stretches={stretches}
              metricKeys={metrics.map(metric => metric.key)}
              groupMetric={groupMetric}
              onGroupMetric={chooseGroup}
              active={activeStretch}
              pinned={pinnedStretch}
              onHover={setHoveredStretch}
              onSelect={setPinnedStretch}
            />
          </div>
        )}
        {metrics.length > 0 && (
          <div className="border-t border-stone-200">
            <PerfectScoreCard metrics={metrics} />
          </div>
        )}
        <div className="hidden border-t border-stone-200 lg:block">
          {trialMeasures}
        </div>
      </div>

      {/* Its own height, not the card's: a resize makes Mapbox redraw and refit. */}
      <div className="h-[420px] overflow-hidden rounded-2xl bg-white shadow-card lg:sticky lg:top-4 lg:h-[600px] lg:self-start">
        {routes.length > 0 ? (
          <LazyRouteMap
            routes={routes}
            highlights={highlights}
            focus={focus}
            className="h-full"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-stone-500">
            {translations.noRoute}
          </div>
        )}
      </div>

      {/* Stacked, the measures come after the map they point at. */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-card lg:hidden">
        {trialMeasures}
      </div>
    </div>
  )
}
